import { ServiceOperationResult } from './../models/api/serviceOperationResult';
import { apiCallWithAuth } from "../helper/apiManager";
import { IUniqueId, IUniqueIdPDTO } from "../models/_uniqueId";
import { IDataOperationResult } from "../models/api/dataOperationResult";
import { IFilter } from "../models/filters/filter";
import { API_CENTRE } from "./config";

export default abstract class _o2sService {
    /**
     * URL de base pour les appels API
     * cette URL doit être surchargé dans les classes filles afin de correspondre à l'API ciblée
     */
    protected static mainUrl: string = API_CENTRE + "/api/v1";

    /**
     * Permet de mapper les filtres de recherche sur les URL des appels API
     * cette fonction peut être surchagé dans les classes filles pour ajouter des filtres spécifiques
     * @param filter données de filtre
     * @param url url initial de l'appel API
     * @returns url après application des filtres
     */
    protected static getUrlFilter<F extends IFilter>(filter?: F): string {
        let url = this.mainUrl;
        if (filter != null) {
            if (filter.include != undefined)
                url += `/search?Include=${filter.include}`;
            else {
                url += "/search?Include=*";
            }
            url += `&PageNumber=${filter.page}&PageSize=${filter.pageSize}`;
            if (filter.search != undefined)
                url += `&GlobalSearch=${filter.search}`;
            if (filter.sort != undefined)
                url += `&Sort=${filter.sort}`;
            if (filter.id != undefined)
                url += `&Id=${filter.id}`;
            if ( filter.active != undefined)
                url += `&Active=${filter.active}`;
            else {
                url += `&Active=true`;
            }
        }
        return url;
    }

    /**
     * Récupération d'un élément par son identifiant
     * @param id identifiant de l'élément
     * @returns un objet contenant les données de l'élément ou une erreur
     */
    public static async getById<T extends IUniqueId>(id: number): Promise<ServiceOperationResult<T | IDataOperationResult<T>>> {
        try {
            let result = await apiCallWithAuth(this.mainUrl + "/" + id, "get");
            return this.ReturnObject(result);
        } catch (err: any) { // TODO : A typer
            return this.ReturnError(err);
        }
    }

    /**
     * Récupération d'un élément par son identifiant
     * @param id identifiant de l'élément
     * @returns un objet contenant les données de l'élément ou une erreur
     */
    public static async getByIdOther<T extends IUniqueId>(id: number, otherObject: string): Promise<ServiceOperationResult<T | IDataOperationResult<T>>> {
        try {
            let result = await apiCallWithAuth(this.mainUrl + "/" + otherObject + "/" + id, "get");
            return this.ReturnObject(result);
        } catch (err: any) { // TODO : A typer
            return this.ReturnError(err);
        }
    }

    /**
     * Retourne l'erreur retourné dans l'exception
     * @param err l'exception
     * @returns
     */
    protected static ReturnError<T extends IUniqueId>(err: any): ServiceOperationResult<IDataOperationResult<T>> {
        return new ServiceOperationResult<IDataOperationResult<T>>(true, { errors: { REQUEST_ERRROR: err.message } } as IDataOperationResult<T>);
    }

    protected static ReturnAttributError<T>(err: any): ServiceOperationResult<T> {
        return new ServiceOperationResult<T>(true, err.message);
    }

    /**
     * Retourne l'objet ou le DataOperationResult retourné par l'appel API
     * @param result le resultat de l'appel API
     * @returns
     */
    protected static ReturnObject<T extends IUniqueId>(result: any): ServiceOperationResult<T | IDataOperationResult<T>> {
        if (result.status >= 200 && result.status <= 300)
            return new ServiceOperationResult<T>(false, result.data as T);
        else
            return new ServiceOperationResult<IDataOperationResult<T>>(true, result.data as IDataOperationResult<T>);
    }

    protected static ReturnAttribut<T>(result: any): ServiceOperationResult<T> {
        if (result.status >= 200 && result.status <= 300)
            return new ServiceOperationResult<T>(false, result.data as T);
        else
            return new ServiceOperationResult<T>(true, result.data as T);
    }

    protected static ReturnListObject<T extends IUniqueId>(result: any): ServiceOperationResult<T[] | IDataOperationResult<T>> {
        if (result.status >= 200 && result.status <= 300)
            return new ServiceOperationResult<T[]>(false, result.data as T[]);
        else
            return new ServiceOperationResult<IDataOperationResult<T>>(true, result.data as IDataOperationResult<T>);
    }

    /**
     * Récupération de tout les éléments en fonction des filtres
     * @param filter Le filtre permettant de faire la sélection des éléments
     * @returns un objet contenant les données des éléments ou une erreur
     */
    public static async getAll<f extends IFilter, l extends IUniqueId>(filter: f): Promise<ServiceOperationResult<l[] | IDataOperationResult<l>>> {
        let url = this.getUrlFilter(filter);
        try {
            let result = await apiCallWithAuth(url, "get");
            return this.ReturnListObject<l>(result);
        } catch (err: any) {
            return this.ReturnError(err);
        }
    }

    /**
     * Création d'un élément en fonction de ses données
     * @param dataObj l'objet à créer
     * @returns un objet contenant les données de l'élément créé ou une erreur
     */
    public static async create<T extends IUniqueId, R extends IUniqueId>(dataObj: T): Promise<ServiceOperationResult<R | IDataOperationResult<R>>> {
        try {
            let objDto = this.toDTO(dataObj);
            let result = await apiCallWithAuth(this.mainUrl, "post", objDto);
            return this.ReturnObject(result);
        } catch (err) {
            return this.ReturnError(err);
        }
    }

    /**
     * Création en batch d'éléments en fonction de leurs données
     * @param dataObjs liste des objets à créer
     * @returns un objet contenant les données des éléments créés ou une erreur
     */
    public static async batchCreate<T extends IUniqueId, R extends IUniqueId>(dataObjs: T[]): Promise<ServiceOperationResult<R[] | IDataOperationResult<R>>> {
        try {
            let objDtos = dataObjs.map(this.toDTO);
            let result = await apiCallWithAuth(this.mainUrl + "/batch", "post", objDtos);
            return this.ReturnListObject<R>(result);
        } catch (err) {
            return this.ReturnError(err);
        }
    }

    /**
     * Création en batch d'éléments en fonction de leurs données
     * @param dataObjs liste des objets à créer
     * @returns un objet contenant les données des éléments créés ou une erreur
     */
    public static async batchUpdate<T extends IUniqueId, R extends IUniqueId>(dataObjs: T[]): Promise<ServiceOperationResult<R[] | IDataOperationResult<R>>> {
        try {
            let objDtos = dataObjs.map(this.toDTO);
            let result = await apiCallWithAuth(this.mainUrl + "/batch", "put", objDtos);
            return this.ReturnListObject<R>(result);
        } catch (err) {
            return this.ReturnError(err);
        }
    }

    /**
     * Mise à jour d'un élément en fonction de ses données, passe un formulaire en entrée et récupère l'objet mise à jour
     * @param dataObj l'objet à mettre à jour de type formulaire
     * @returns un objet contenant les données de l'élement mis à jour ou une erreur
     */
    public static async update<o extends IUniqueId, R extends IUniqueId>(dataObj: o): Promise<ServiceOperationResult<R | IDataOperationResult<R>>> {
        try {
            let objDto = this.toDTO(dataObj);
            let result = await apiCallWithAuth(this.mainUrl + "/" + dataObj.id, "put", objDto);
            return this.ReturnObject(result);
        } catch (err) {
            return this.ReturnError(err);
        }
    }

    /**
    * Mise à jour d'un élément en fonction de ses données, passe un formulaire en entrée et récupère l'objet mise à jour
    * @param dataObj l'objet à mettre à jour de type formulaire
    * @returns un objet contenant les données de l'élement mis à jour ou une erreur
    */
    public static async patch<o extends IUniqueId, R extends IUniqueId>(dataObj: o,force:boolean = false): Promise<ServiceOperationResult<R | IDataOperationResult<R>>> {
        try {
            let objDto = this.toDTO(dataObj);
            let result;
            if ( force) {
                result = await apiCallWithAuth(this.mainUrl + "/" + dataObj.id + "?force=true", "patch", objDto);
            } else {
                result = await apiCallWithAuth(this.mainUrl + "/" + dataObj.id, "patch", objDto);
            }
            return this.ReturnObject(result);
        } catch (err) {
            return this.ReturnError(err);
        }
    }

    public static async delete<R extends IUniqueId>(id: number,force:boolean = false): Promise<ServiceOperationResult<R | IDataOperationResult<R>>> {
        try {
            let result;
            if ( force) {
                result = await apiCallWithAuth(this.mainUrl + "/" + id + "?force=true", "delete");
            } else {
                result = await apiCallWithAuth(this.mainUrl + "/" + id, "delete");
            }
            return this.ReturnObject(result);
        } catch (err) {
            return this.ReturnError(err);
        }
    }

    public static async batchDelete<R extends IUniqueId>(ids: number[], force:boolean = false): Promise<ServiceOperationResult<R[] | IDataOperationResult<R>>> {
        try {
            let result;
            if (force) {
                console.log("Force delete",ids);
                result = await apiCallWithAuth(this.mainUrl + "/batch?force=true", "batchdelete", ids);
            } else {
                result = await apiCallWithAuth(this.mainUrl + "/batch", "batchdelete", ids);
            }
            return this.ReturnListObject<R>(result);
        } catch(err) {
            return this.ReturnError(err);
        }
    }

    /**
     * Transformation d'un objet formulaire en DTO
     * @param obj l'objet à transformer
     * @returns
     */
    protected static toDTO(obj: IUniqueId): IUniqueIdPDTO {
        const dto = {} as IUniqueIdPDTO;
        Object.assign(dto, obj);
        return dto;
    }
    /**
     * Suppression d'un élément en fonction de son identifiant
     * @param id identifiant de l'élément à supprimer
     * @returns un boolean avec l'erreur ou non
     */
    /*
    public static async delete(id:number) : Promise<ServiceOperationResult<boolean>>
    {
        try {
            let result = await apiCallWithAuth(this.mainUrl + "/" + id, "delete");
            return this.ReturnObject(result);
        } catch (err) {
            return this.ReturnError(err);
        }
    }
    */

    /**
     * Permet de concaténer des paramètres en une url
     * @param pathParameters Paramètres du chemin d'accès
     * @returns Url
     */
    private static buildPath(pathParameters: string[]): string {
        let url = this.mainUrl;

        if (pathParameters.length > 0) {
            for (const param of pathParameters) {
                url += `/${param}`;
            }
        }

        return url;
    }

    /**
     * Permet de faire un appel API en passant des paramètres de chemins d'accès (mainUrl/param1/param2...)
     * @param pathParams Paramètres du chemin d'accès
     * @param method Méthode HTTP
     * @param data Objet à mettre dans la requête
     * @returns Objet contenant les données de retour ou une erreur
     */
    public static async pathParamApiCall<T extends IUniqueId, O = {}>(pathParams: string[], method: string = "get", data?: O): Promise<ServiceOperationResult<T | IDataOperationResult<T>>> {
        let url = this.buildPath(pathParams);
        let result;

        try {
            if (data != undefined) {
                result = await apiCallWithAuth(url, method, data);
            }
            else {
                result = await apiCallWithAuth(url, method);
            }

            return this.ReturnObject(result);
        }
        catch (err: any) {
            return this.ReturnError(err);
        }
    }

    /**
     * Permet de faire un appel API pour une liste en passant des paramètres de chemins d'accès (mainUrl/param1/param2...)
     * @param pathParams Paramètres du chemin d'accès
     * @param method Méthode HTTP
     * @param data Objet à mettre dans la requête
     * @returns Objet contenant la liste de données de retour ou une erreur
     */
    public static async pathParamApiListCall<T extends IUniqueId, O = {}>(pathParams: string[], method: string = "get", data?: O): Promise<ServiceOperationResult<T[] | IDataOperationResult<T>>> {
        let url = this.buildPath(pathParams);
        let result;

        try {
            if (data != undefined) {
                result = await apiCallWithAuth(url, method, data);
            }
            else {
                result = await apiCallWithAuth(url, method);
            }

            return this.ReturnListObject<T>(result);
        }
        catch (err: any) {
            return this.ReturnError(err);
        }
    }
}
